import type { Command, CommandOutput } from '../../types/commands'
import { colorize, formatHighlight } from '../colorScheme'
import { highlightSyntax } from '../syntaxHighlighter'
import { dividers } from '../asciiArt'
import postsData from '../../../blog/posts.json'

export const blogCommand: Command = {
  name: 'blog',
  description: 'Read thought log entries',
  aliases: ['thoughts', 'writing'],
  category: 'info',
  execute: async () => {
    const sortedPosts = [...postsData].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )

    // Fetch all post content in parallel
    const postContents = await Promise.all(
      sortedPosts.map(async (post) => {
        try {
          const response = await fetch(`/blog/posts/${post.slug}.md`)
          if (response.ok) return await response.text()
        } catch { /* skip failed fetches */ }
        return null
      })
    )

    const outputs: CommandOutput[] = []

    // Header
    const headerHtml = highlightSyntax(`
╔═══════════════════════════════════════════════════════════╗
║    📔 THOUGHT LOG - Reflections on Tech & Development     ║
╚═══════════════════════════════════════════════════════════╝

  ${sortedPosts.length} ${sortedPosts.length === 1 ? 'post' : 'posts'} · most recent first
  Scroll down to read all entries

${dividers.dash}
`, 'header.txt')

    outputs.push({
      type: 'info',
      content: <div dangerouslySetInnerHTML={{ __html: headerHtml }} />,
    })

    // Each post as its own entry
    sortedPosts.forEach((post, i) => {
      const content = postContents[i]
      if (!content) return

      const tags = post.tags.length > 0
        ? post.tags.map(tag => colorize(`#${tag}`, 'cyan')).join(' ') + '\n'
        : ''

      const meta = `┌─────────────────────────────────────────────────────────┐
│  ${formatHighlight(post.title)}
│  ${colorize(post.date, 'brightBlack')}${post.readTime ? ' | ' + colorize(post.readTime, 'magenta') : ''} | ${post.category}
│  ${tags}└─────────────────────────────────────────────────────────┘
`

      const highlighted = highlightSyntax(content, `${post.slug}.md`)

      outputs.push({
        type: 'info',
        content: (
          <div>
            <div dangerouslySetInnerHTML={{ __html: meta }} />
            <div dangerouslySetInnerHTML={{ __html: highlighted }} />
            {i < sortedPosts.length - 1 && (
              <pre className="command-output-pre">{'\n' + dividers.dash + '\n'}</pre>
            )}
          </div>
        ),
      })
    })

    return outputs
  },
}
